import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { usePrompt } from '@/contexts/PromptContext'

interface ToneStepProps {
  value?: any
  onChange?: (value: any) => void
  readOnly?: boolean
}

// Define the tone data structure as specified
const TONE_OPTIONS = {
  casual_professional: 5, // 0-10 score (Casual <> Professional)
  friendly_serious: 5, // 0-10 score (Friendly <> Serious)
  inviting_neutral: 5, // 0-10 score (Inviting <> Neutral)
  calm_energetic: 5, // 0-10 score (Calm <> Energetic)
  // Additional tone options
  adventurous: false,
  commanding: false,
  conversational: false,
  educational: false,
  emotional: false,
  exciting: false,
  fact_based: false,
  humorous: false,
  industrious: false,
  informative: false,
  innovative: false,
  storytelling: false,
  testimonial: false,
}

const ToneStep: React.FC<ToneStepProps> = ({
  value: externalValue,
  onChange: externalOnChange,
  readOnly = false
}) => {
  const { t } = useTranslation('steps')
  const [toneData, setToneData] = useState(TONE_OPTIONS)
  const { promptData, updatePrompt, loading } = usePrompt()

  // Load existing tone data when promptData is available
  useEffect(() => {
    if (promptData?.tone_scores && !externalValue) {
      setToneData(promptData.tone_scores)
    }
  }, [promptData?.tone_scores, externalValue])

  // Update tone immediately without debouncing
  const updateTone = async (newToneData: any) => {
    try {
      await updatePrompt({ tone_scores: newToneData })
    } catch (error) {
      console.error('Error updating tone:', error)
    }
  }

  const updateToneData = (updates: Partial<typeof TONE_OPTIONS>) => {
    if (readOnly) return
    
    const newToneData = { ...toneData, ...updates }
    setToneData(newToneData)
    
    // If we're in refine mode (external onChange), call the external handler
    if (externalOnChange) {
      externalOnChange(newToneData)
      return
    }
    
    // Update database immediately
    updateTone(newToneData)
  }

  // Extract selected additional tones
  const additionalTones = [
    'adventurous',
    'commanding',
    'conversational',
    'educational',
    'emotional',
    'exciting',
    'fact_based',
    'humorous',
    'industrious',
    'informative',
    'innovative',
    'storytelling',
    'testimonial'
  ].filter(key => toneData[key as keyof typeof toneData] === true)

  // Extract individual values for UI (default to 5 for centered position)
  const casualProfessionalScore = toneData.casual_professional ?? 5
  const friendlySeriousScore = toneData.friendly_serious ?? 5
  const invitingNeutralScore = toneData.inviting_neutral ?? 5
  const calmEnergeticScore = toneData.calm_energetic ?? 5

  return (
    <div className="text-center">
      <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-4 mt-2 sm:mt-6 px-4">
        {t('tone.title')}
      </h2>

      <p className="text-gray-600 mb-4 max-w-lg mx-auto px-4 text-xs sm:text-base">
        {t('tone.description')}
      </p>

      <div className="max-w-lg mx-auto mb-6 sm:mb-8 px-4">
        <ul className="text-left list-disc list-inside space-y-2 text-gray-600">
          <li className="text-xs sm:text-base">
            {t('tone.questions.formal')}
          </li>
          <li className="text-xs sm:text-base">
            {t('tone.questions.friendly')}
          </li>
          <li className="text-xs sm:text-base">
            {t('tone.questions.warm')}
          </li>
          <li className="text-xs sm:text-base">
            {t('tone.questions.energetic')}
          </li>
          <li className="text-xs sm:text-base">
            {t('tone.questions.feeling')}
          </li>
        </ul>
      </div>

      <div className="max-w-md mx-auto space-y-4 sm:space-y-6 mb-6 sm:mb-8 px-4">
        {/* Casual <> Professional Slider */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs sm:text-sm text-gray-600">
            <span>{t('tone.sliders.casual')}</span>
            <span>{t('tone.sliders.professional')}</span>
          </div>
          <div className="relative">
            <input
              type="range"
              min="0"
              max="10"
              value={casualProfessionalScore}
              onChange={(e) => updateToneData({ casual_professional: Number(e.target.value) })}
              disabled={readOnly || loading}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            />
          </div>
        </div>

        {/* Friendly <> Serious Slider */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs sm:text-sm text-gray-600">
            <span>{t('tone.sliders.friendly')}</span>
            <span>{t('tone.sliders.serious')}</span>
          </div>
          <div className="relative">
            <input
              type="range"
              min="0"
              max="10"
              value={friendlySeriousScore}
              onChange={(e) => updateToneData({ friendly_serious: Number(e.target.value) })}
              disabled={readOnly || loading}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            />
          </div>
        </div>

        {/* Inviting <> Neutral Slider */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs sm:text-sm text-gray-600">
            <span>{t('tone.sliders.inviting')}</span>
            <span>{t('tone.sliders.neutral')}</span>
          </div>
          <div className="relative">
            <input
              type="range"
              min="0"
              max="10"
              value={invitingNeutralScore}
              onChange={(e) => updateToneData({ inviting_neutral: Number(e.target.value) })}
              disabled={readOnly || loading}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            />
          </div>
        </div>

        {/* Calm <> Energetic Slider */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs sm:text-sm text-gray-600">
            <span>{t('tone.sliders.calm')}</span>
            <span>{t('tone.sliders.energetic')}</span>
          </div>
          <div className="relative">
            <input
              type="range"
              min="0"
              max="10"
              value={calmEnergeticScore}
              onChange={(e) => updateToneData({ calm_energetic: Number(e.target.value) })}
              disabled={readOnly || loading}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            />
          </div>
        </div>

        {/* Additional Tone Multi-Select */}
        <div className="space-y-2">
          <div className="text-left text-xs sm:text-sm text-gray-600">{t('tone.others')}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              'adventurous',
              'commanding',
              'conversational',
              'educational',
              'emotional',
              'exciting',
              'fact_based',
              'humorous',
              'industrious',
              'informative',
              'innovative',
              'storytelling',
              'testimonial'
            ].map((toneKey) => (
              <button
                key={toneKey}
                type="button"
                onClick={() => {
                  if (readOnly) return
                  updateToneData({ [toneKey]: !toneData[toneKey as keyof typeof toneData] })
                }}
                disabled={readOnly || loading}
                className={`p-2 text-xs sm:text-sm rounded border transition-colors ${
                  additionalTones.includes(toneKey)
                    ? 'border-gray-800 bg-gray-50 text-gray-800'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                } ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {t(`tone.additionalTones.${toneKey}`)}
              </button>
            ))}
          </div>
          
          {/* Selected Tones Display */}
          {additionalTones.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {additionalTones.map((tone) => (
                <Badge key={tone} variant="secondary" className="text-xs">
                  {tone.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ToneStep
